import { useState, useEffect, useCallback } from 'react'
import { Alert, Box, LinearProgress, Typography, useTheme } from '@mui/material'
import { useTasksInOrg } from './hooks/use-org-tasks'
import { TaskListControls } from './components/task-list-controls'
import { TaskGroup } from './components/task-group'
import { useOrgContextDispatch, setHoveredTaskGap as setHoveredTaskGapAction } from '~/common/context/organization/org-context'
import {
  GROUP_STRATEGIES,
  SORT_STRATEGIES,
  DATE_OFFSET_STRATEGIES,
  groupTasks,
  sortTasks,
  filterTasksByDateOffset,
  applyScheduledState
} from './utils/task-grouping-utils'
import { useCurrentUser } from '~/common/use-current-user'
import { useFeatures } from '~/common/use-features'

const FALLBACK_READY_WINDOW_OFFSET_DAYS = 7

/**
 * Task List - Main orchestrator component
 *
 * Features:
 * - Hierarchical task fetching (farm → plot → patch → crops)
 * - Date-based filtering (show tasks up to 1w/2w/1m/3m/all from now)
 * - Grouping by type or target (org vs crop tasks)
 * - Sorting by date, alphabetical, or status
 * - Collapsible groups
 */
export const TaskList = () => {
  const theme = useTheme()
  const { tasks, loading, error, updateTaskInState } = useTasksInOrg()
  const dispatch = useOrgContextDispatch()
  const [groupStrategy, setGroupStrategy] = useState(GROUP_STRATEGIES.NONE)
  const [sortStrategy, _setSortStrategy] = useState(SORT_STRATEGIES.DATE) // TODO may not need anymore
  const [dateOffset, setDateOffset] = useState(DATE_OFFSET_STRATEGIES.TWO_WEEKS)
  const [expandedGroupIds, setExpandedGroupIds] = useState([])
  const [groupedTasks, setGroupedTasks] = useState([])
  
  const { userPreferences } = useCurrentUser()
  const { features } = useFeatures()

  useEffect(() => {
    function compute() {
      if (!tasks.length) return

      // Exclude multi-submission crop tasks (e.g. harvest) — handled via timeline tracker only
      // Org tasks are always included regardless of single_completion
      const singleCompletionTasks = tasks.filter(t =>
        t.crop_id === null || t.task_types?.single_completion !== false
      )

      // apply the state override based on current date and task window
      const scheduledTasks = applyScheduledState(
        singleCompletionTasks,
        userPreferences.ready_window_offset_days || FALLBACK_READY_WINDOW_OFFSET_DAYS
      )

      // Apply date filtering first
      const filtered = filterTasksByDateOffset(scheduledTasks, dateOffset)

      // Apply sorting if ungrouped, otherwise groups will handle sorting
      const sorted = groupStrategy === GROUP_STRATEGIES.NONE
        ? sortTasks(filtered, sortStrategy)
        : filtered

      setGroupedTasks(groupTasks(sorted, groupStrategy))
    }
    compute()
  }, [tasks, userPreferences, dateOffset, groupStrategy, sortStrategy])

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroupIds(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    )
  }, [])

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {features['task_list_controls'] && (
        <Box
          sx={{
            borderBottom: `0.5px solid ${theme.palette.primary[300]}`,
            pb: 1,
            boxShadow: `0 1px 5px -2px ${theme.palette.primary[400]}`,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <TaskListControls
            groupStrategy={groupStrategy}
            setGroupStrategy={setGroupStrategy}
            dateOffset={dateOffset}
            setDateOffset={setDateOffset}
            dispatch={dispatch}
            setHoveredTaskGapAction={setHoveredTaskGapAction}
          />
        </Box>
      )}

      {error && <Alert title='Error fetching tasks, please refresh' severity='error'/>}

      {groupedTasks.length === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography color="text.secondary">No tasks in filter range</Typography>
        </Box>
      )}

      {groupedTasks.length > 0 && (
        <Box sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          {loading && (
            <Box sx={{ position: 'relative' }}>
              <LinearProgress
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 25,
                }}
              />
            </Box>
          )}
          {groupedTasks.map(group => (
            <TaskGroup
              key={group.id}
              group={group}
              isExpanded={expandedGroupIds.includes(group.id)}
              onToggle={toggleGroup}
              onUpdateTask={updateTaskInState}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
